import { create } from 'zustand'

/**
 * UI-Präferenzen: Schriftgröße, Modus, Performance, Thema.
 *
 * Bewusst NICHT im Server-Store: Diese Einstellungen müssen beim allerersten Paint
 * stehen, bevor irgendein Request beantwortet ist — sonst springt die Oberfläche
 * für genau die Nutzer, die auf große Schrift angewiesen sind. Sie werden später
 * zusätzlich am Konto gespiegelt, damit ein neues Gerät sie übernimmt.
 */

export type Scale = 'normal' | 'large' | 'xlarge'
export type Mode = 'standard' | 'einfach'
export type Perf = 'auto' | 'low'
export type Theme = 'system' | 'light' | 'dark'

export interface Prefs {
  scale: Scale
  mode: Mode
  perf: Perf
  theme: Theme
}

const DEFAULTS: Prefs = { scale: 'normal', mode: 'standard', perf: 'auto', theme: 'system' }
const STORAGE_KEY = 'fh.prefs'

/**
 * Erkennt schwache Geräte und schaltet den Performance-Modus vor. Nutzer können
 * jederzeit überstimmen — die Heuristik entscheidet nur den Startwert (Anf. 67).
 */
export function detectLowEnd(): boolean {
  const nav = navigator as Navigator & {
    deviceMemory?: number
    connection?: { saveData?: boolean; effectiveType?: string }
  }
  if (nav.connection?.saveData) return true
  if (typeof nav.deviceMemory === 'number' && nav.deviceMemory <= 2) return true
  if (nav.hardwareConcurrency && nav.hardwareConcurrency <= 2) return true
  const type = nav.connection?.effectiveType
  if (type === 'slow-2g' || type === '2g' || type === '3g') return true
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

export function loadPrefs(): Prefs {
  let stored: Partial<Prefs> = {}
  try {
    stored = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}') as Partial<Prefs>
  } catch {
    // Privater Modus, blockierte Site-Daten, kaputter Eintrag — Defaults sind richtig.
  }
  const prefs = { ...DEFAULTS, ...stored }
  if (!stored.perf && detectLowEnd()) prefs.perf = 'low'
  return prefs
}

/** Schreibt die Präferenzen als Attribute an <html>, wo das CSS sie liest. */
export function applyPrefs(p: Prefs): void {
  const root = document.documentElement
  root.dataset.scale = p.scale
  root.dataset.mode = p.mode
  root.dataset.perf = p.perf
  if (p.theme === 'system') delete root.dataset.theme
  else root.dataset.theme = p.theme
}

interface PrefsStore extends Prefs {
  set<K extends keyof Prefs>(key: K, value: Prefs[K]): void
}

export const usePrefs = create<PrefsStore>((set, get) => ({
  ...loadPrefs(),
  set(key, value) {
    set({ [key]: value } as Pick<Prefs, typeof key>)
    const { scale, mode, perf, theme } = get()
    const next = { scale, mode, perf, theme, [key]: value } as Prefs
    applyPrefs(next)
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
    } catch {
      // Speichern kann fehlschlagen; die Einstellung gilt trotzdem für diese Sitzung.
    }
  },
}))
